import type {AreaEffect} from "@/encounter/model/AreaEffect.ts";
import type Position from "@/util/Position.ts";
import {PIXELS_PER_METER} from "@/dungeons/geometry/Coordinates.ts";

export const MIN_EFFECT_SIZE = 0.5;
export const MAX_EFFECT_SIZE = 30;
export const EFFECT_SIZE_STEP = 0.5;

export type AreaEffectResizeDimension = "length" | "width" | "radius";

export type AreaEffectResizeHandle = {
    dimension: AreaEffectResizeDimension;
    position: Position;
}

export type AreaEffectRotationHandle = {
    connectorStart: Position;
    position: Position;
}

const snapEffectSize = (value: number, maximum = MAX_EFFECT_SIZE) => Math.min(
    maximum,
    Math.max(MIN_EFFECT_SIZE, Math.round(value / EFFECT_SIZE_STEP) * EFFECT_SIZE_STEP),
);

const distanceFromOrigin = (position: Position) => Math.hypot(position.x, position.y);

/** Converts a world position into the rotated local coordinate system of an effect. */
export function toAreaEffectLocalPosition(
    pointer: Position,
    origin: Position,
    rotationDegrees: number,
): Position {
    const radians = -rotationDegrees * Math.PI / 180;
    const x = pointer.x - origin.x;
    const y = pointer.y - origin.y;

    return {
        x: Math.cos(radians) * x - Math.sin(radians) * y,
        y: Math.sin(radians) * x + Math.cos(radians) * y,
    };
}

export function setAreaEffectLength(effect: AreaEffect, value: number): AreaEffect {
    return {...effect, length: snapEffectSize(value)};
}

export function setAreaEffectWidth(effect: AreaEffect, value: number): AreaEffect {
    const maximum = effect.type === "Arc"
        ? effect.radius ?? MIN_EFFECT_SIZE
        : MAX_EFFECT_SIZE;
    return {...effect, width: snapEffectSize(value, maximum)};
}

export function setAreaEffectRadius(effect: AreaEffect, value: number): AreaEffect {
    const radius = snapEffectSize(value);
    if (effect.type !== "Arc" || effect.width === undefined) return {...effect, radius};
    return {...effect, radius, width: Math.min(effect.width, radius)};
}

/** Calculates the resized effect from a handle position in effect-local coordinates. */
export function resizeAreaEffect(
    effect: AreaEffect,
    dimension: AreaEffectResizeDimension,
    pointer: Position,
): AreaEffect {
    switch (dimension) {
        case "length":
            return setAreaEffectLength(effect, pointer.x / PIXELS_PER_METER);
        case "radius":
            return setAreaEffectRadius(effect, distanceFromOrigin(pointer) / PIXELS_PER_METER);
        case "width":
            return effect.type === "Arc"
                ? setAreaEffectWidth(
                    effect,
                    (effect.radius ?? MIN_EFFECT_SIZE) - distanceFromOrigin(pointer) / PIXELS_PER_METER,
                )
                : setAreaEffectWidth(effect, 2 * Math.abs(pointer.y) / PIXELS_PER_METER);
    }
}

export function getAreaEffectResizeHandles(effect: AreaEffect): AreaEffectResizeHandle[] {
    switch (effect.type) {
        case "Rect": {
            const length = (effect.length ?? MIN_EFFECT_SIZE) * PIXELS_PER_METER;
            const width = (effect.width ?? MIN_EFFECT_SIZE) * PIXELS_PER_METER;
            return [
                {dimension: "length", position: {x: length, y: 0}},
                {dimension: "width", position: {x: length / 2, y: width / 2}},
            ];
        }
        case "Sphere": {
            const radius = (effect.radius ?? MIN_EFFECT_SIZE) * PIXELS_PER_METER;
            return [{dimension: "radius", position: {x: radius, y: 0}}];
        }
        case "Arc": {
            const radius = (effect.radius ?? MIN_EFFECT_SIZE) * PIXELS_PER_METER;
            const width = (effect.width ?? MIN_EFFECT_SIZE) * PIXELS_PER_METER;
            return [
                {dimension: "radius", position: {x: radius, y: 0}},
                {dimension: "width", position: {x: Math.max(0, radius - width), y: 0}},
            ];
        }
    }
}

/** Spheres have no visible orientation and therefore do not need a rotation handle. */
export function getAreaEffectRotationHandle(
    effect: AreaEffect,
    offset: number,
): AreaEffectRotationHandle | undefined {
    if (effect.type === "Sphere") return undefined;

    const forwardExtent = (effect.type === "Rect" ? effect.length : effect.radius)
        ?? MIN_EFFECT_SIZE;
    const connectorStart = {x: forwardExtent * PIXELS_PER_METER, y: 0};
    return {
        connectorStart,
        position: {x: connectorStart.x + offset, y: 0},
    };
}

/** Returns the visual center used for the lock handle. */
export function getAreaEffectLockHandlePosition(effect: AreaEffect): Position {
    switch (effect.type) {
        case "Rect":
            return {
                x: (effect.length ?? MIN_EFFECT_SIZE) * PIXELS_PER_METER / 2,
                y: 0,
            };
        case "Sphere":
            return {x: 0, y: 0};
        case "Arc":
            return {
                x: (
                    (effect.radius ?? MIN_EFFECT_SIZE)
                    - (effect.width ?? MIN_EFFECT_SIZE) / 2
                ) * PIXELS_PER_METER,
                y: 0,
            };
    }
}

/** Bounds of the rendered shape, including its current rotation, in world pixels. */
export function getAreaEffectWorldBounds(effect: AreaEffect, origin: Position, rotationDegrees: number) {
    const radius = (effect.radius ?? MIN_EFFECT_SIZE) * PIXELS_PER_METER;
    if (effect.type === "Sphere" || (effect.type === "Arc" && (effect.angle ?? 0) >= 360)) {
        return {left: origin.x - radius, right: origin.x + radius,
            top: origin.y - radius, bottom: origin.y + radius};
    }

    const radians = rotationDegrees * Math.PI / 180;
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);
    const rotate = (point: Position): Position => ({
        x: origin.x + point.x * cos - point.y * sin,
        y: origin.y + point.x * sin + point.y * cos,
    });
    let points: Position[];
    if (effect.type === "Rect") {
        const length = (effect.length ?? MIN_EFFECT_SIZE) * PIXELS_PER_METER;
        const halfWidth = (effect.width ?? MIN_EFFECT_SIZE) * PIXELS_PER_METER / 2;
        points = [
            {x: 0, y: -halfWidth}, {x: 0, y: halfWidth},
            {x: length, y: -halfWidth}, {x: length, y: halfWidth},
        ].map(rotate);
    } else {
        const halfAngle = (effect.angle ?? 0) / 2;
        const innerRadius = Math.max(0, radius - (effect.width ?? MIN_EFFECT_SIZE) * PIXELS_PER_METER);
        const angles = [rotationDegrees - halfAngle, rotationDegrees + halfAngle];
        for (const cardinal of [0, 90, 180, 270]) {
            const difference = ((cardinal - rotationDegrees + 540) % 360) - 180;
            if (Math.abs(difference) <= halfAngle) angles.push(cardinal);
        }
        points = angles.flatMap(angle => [innerRadius, radius].map(distance => ({
            x: origin.x + Math.cos(angle * Math.PI / 180) * distance,
            y: origin.y + Math.sin(angle * Math.PI / 180) * distance,
        })));
    }
    return {
        left: Math.min(...points.map(point => point.x)),
        right: Math.max(...points.map(point => point.x)),
        top: Math.min(...points.map(point => point.y)),
        bottom: Math.max(...points.map(point => point.y)),
    };
}
